/**
 * Variant enrichment helpers: which refs are variants, what a variant asks the
 * AI (the six variant-level fields), and the free, no-AI "copy empty fields
 * from the parent" pre-fill.
 *
 * The six fields are the singular fields the watchlock `variantAggregates`
 * trigger rolls up into the parent's `*Variants` arrays. Everything else on a
 * variant is the product's shared spec, so it comes from the parent, never from
 * a model call.
 */
import type { WatchReference } from '../../types';

/** Suggestion keys a variant is enriched for (mirrors functions VARIANT_ENRICH_KEYS). */
export const VARIANT_FIELD_KEYS = [
  'dialColor',
  'dialFinish',
  'dialStyle',
  'caseMaterial',
  'strapMaterial',
  'calibre',
] as const;
export type VariantFieldKey = (typeof VARIANT_FIELD_KEYS)[number];

/** Columns a variant row shows: the six, plus the tier pick that rides with calibre. */
export const VARIANT_GRID_KEYS: readonly string[] = [...VARIANT_FIELD_KEYS, 'calibreTierId'];

export function isVariantRef(ref: Pick<WatchReference, 'isVariant' | 'parentReferenceId'>): boolean {
  return ref.isVariant === true || !!ref.parentReferenceId?.trim();
}

/**
 * The shop's name for this option. The importer stores it as `nickname`; a
 * store variant's reference also ends "— <option>" ("IRIS-V3 — Satin Blue Iris").
 */
export function variantLabelOf(ref: Pick<WatchReference, 'nickname' | 'reference'>): string | undefined {
  const nick = ref.nickname?.trim();
  if (nick) return nick;
  const ref_ = ref.reference ?? '';
  const at = ref_.lastIndexOf(' — ');
  return at > 0 ? ref_.slice(at + 3).trim() || undefined : undefined;
}

type Loose = Record<string, unknown>;
const group = (r: Loose, g: string): Loose =>
  r[g] && typeof r[g] === 'object' && !Array.isArray(r[g]) ? (r[g] as Loose) : {};

function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as object).length === 0;
  return false;
}

/** A ref's value for one of the six, in the shape the field registry's buildPatch takes. */
export function readVariantField(ref: WatchReference, key: VariantFieldKey): unknown {
  const r = ref as unknown as Loose;
  switch (key) {
    case 'dialColor': {
      const c = group(r, 'dialAndHands').color;
      return isEmpty(c) ? r.dial : c;
    }
    case 'dialFinish':
      return group(r, 'dialAndHands').finish;
    case 'dialStyle':
      return r.dialStyle;
    case 'caseMaterial':
      return group(r, 'case').material;
    case 'strapMaterial':
      return group(r, 'strap').material;
    case 'calibre':
      return r.calibre;
  }
}

/**
 * The parent's value for each of the six the variant is missing. Shown in the
 * grid as "from parent" proposals, so the reviewer sees and ticks them like any
 * other; an AI answer for the same key replaces it.
 */
export function parentVariantFieldProposals(
  variant: WatchReference,
  parent: WatchReference | null | undefined,
): Partial<Record<VariantFieldKey, unknown>> {
  if (!parent) return {};
  const out: Partial<Record<VariantFieldKey, unknown>> = {};
  for (const key of VARIANT_FIELD_KEYS) {
    if (!isEmpty(readVariantField(variant, key))) continue;
    const pv = readVariantField(parent, key);
    if (!isEmpty(pv)) out[key] = pv;
  }
  return out;
}

/**
 * Top-level fields copied from the parent when the variant's is empty — the
 * product's shared spec. Allowlisted, never everything: identity, links,
 * images, prices, provenance, the option label (`nickname`), prose
 * (descriptions are per listing) and the parent's own aggregates never move.
 */
const COPY_TOP_LEVEL = [
  'model',
  'collection',
  'series',
  'watchType',
  'movementType',
  'specialEdition',
  'knownDiscontinuation',
  'bezelType',
  'bezelColors',
  'caseColors',
  'crownType',
  'crystal',
  'glass',
  'hasIntegratedBracelet',
  'functions',
  'features',
  'moduleId',
] as const;

/** Nested groups copied leaf by leaf (each empty leaf on the variant is filled). */
const COPY_GROUPS = ['case', 'strap', 'dialAndHands', 'movement', 'production', 'electronics', 'pocketWatch', 'bezel'] as const;

/** Leaves owned by the six variant-level fields — handled as grid proposals, not copied. */
const VARIANT_FIELD_PATHS = new Set(['dialAndHands.color', 'dialAndHands.finish', 'case.material', 'strap.material']);

/**
 * The no-AI pre-fill: a dotted-path, fill-empty patch carrying the parent's
 * shared spec onto a variant. Excludes the six variant-level fields (proposals
 * in the grid) and calibre (one of the six). Empty when there is nothing to copy.
 */
export function parentCopyPatch(
  variant: WatchReference,
  parent: WatchReference | null | undefined,
): Record<string, unknown> {
  if (!parent) return {};
  const v = variant as unknown as Loose;
  const p = parent as unknown as Loose;
  const patch: Record<string, unknown> = {};
  for (const key of COPY_TOP_LEVEL) {
    if (isEmpty(v[key]) && !isEmpty(p[key])) patch[key] = p[key];
  }
  for (const g of COPY_GROUPS) {
    const pv = p[g];
    // A non-object group (legacy string `bezel`, say) copies whole.
    if (!pv || typeof pv !== 'object' || Array.isArray(pv)) {
      if (g === 'bezel' && isEmpty(v[g]) && !isEmpty(pv)) patch[g] = pv;
      continue;
    }
    // The variant holds this group in another shape (a legacy string): writing
    // nested leaves into it would fail, so leave the group alone.
    if (!isEmpty(v[g]) && (typeof v[g] !== 'object' || Array.isArray(v[g]))) continue;
    const vg = group(v, g);
    for (const [leaf, value] of Object.entries(pv as Loose)) {
      const path = `${g}.${leaf}`;
      if (VARIANT_FIELD_PATHS.has(path)) continue;
      if (isEmpty(vg[leaf]) && !isEmpty(value)) patch[path] = value;
    }
  }
  return patch;
}
